import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"
import SelectInput from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SelectInput"
import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"
import {
	formatBoardStageOptions,
	formatManageContactInBoardOptions,
	getCurrentManageContactInBoardData,
	getCurrentManageContactInBoardOption
} from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"
import {
	ChatBotFlowBlockContent,
	ManageContactInBoardActionToManageContactInBoardData
} from "@/protocols/chatBot"
import { BoardSummary } from "@/services/BoardService"
import { Grid } from "@material-ui/core"
import { Loading } from "letalkui"
import React from "react"

type ManageContactInBoardSectionEditorProps = {
	onSave: (chatBotFlowBlockContent: ChatBotFlowBlockContent) => void
	chatBotFlowBlockContent: ChatBotFlowBlockContent
	boards: Array<BoardSummary>
	isLoading: boolean
}

const ManageContactInBoardSectionEditor: React.FC<ManageContactInBoardSectionEditorProps> = (props) => {
	const {
		chatBotFlowBlockContent,
		onSave,
		children,
		boards,
		isLoading
	} = props

	const contentEditor = useContentEditor({ chatBotFlowBlockContent })
	const formattedManageContactInBoardOptions = formatManageContactInBoardOptions()
	const currentManageContactInBoardOption = getCurrentManageContactInBoardOption(contentEditor.content)

	const getRemoveContactFromBoardSelectorInput = () => {
		const currentManageContactInBoardData = getCurrentManageContactInBoardData(contentEditor.content) as ManageContactInBoardActionToManageContactInBoardData["remove-contact-from-board"]

		return (
			<Grid item xs={12}>
				<Grid container>
					<SelectInput
						multiple={false}
						defaultOption={currentManageContactInBoardData?.board}
						onChange={board => {
							contentEditor.changeContent({
								manageContactInBoardData: {
									data: {
										board: {
											id: board?.id,
											name: board?.name
										}
									}
								}
							})
						}}
						placeholder={"Selecione um funil"}
						options={boards}
					/>
				</Grid>
			</Grid>
		)
	}

	const getAddOrMoveContactToBoardSelectorInput = () => {
		const currentManageContactInBoardData = getCurrentManageContactInBoardData(contentEditor.content) as ManageContactInBoardActionToManageContactInBoardData["add-or-move-contact-to-board-stage"]

		const formattedBoardStageOptions = formatBoardStageOptions(boards, contentEditor.content)

		return (
			<>
				<Grid item xs={12}>
					<Grid container>
						<SelectInput
							multiple={false}
							defaultOption={currentManageContactInBoardData?.board}
							onChange={board => {
								contentEditor.changeContent({
									manageContactInBoardData: {
										data: {
											...(board ? {
												board: {
													id: board?.id,
													name: board?.name
												}
											} : {
												board: null
											})
										}
									}
								})
							}}
							placeholder="Nome do funil"
							options={boards}
							noOptionsText="Sem opções"
						/>
					</Grid>
				</Grid>

				{currentManageContactInBoardData?.board && (
					<Grid item xs={12}>
						<Grid container>
							<SelectInput
								multiple={false}
								defaultOption={currentManageContactInBoardData?.stage}
								onChange={stage => {
									contentEditor.changeContent({
										manageContactInBoardData: {
											data: {
												...(stage ? {
													stage: {
														id: stage.id,
														name: stage?.name
													}
												} : {
													stage: null
												})
											}
										}
									})
								}}
								placeholder="Nome da etapa"
								options={formattedBoardStageOptions}
								noOptionsText="Sem opções"
							/>
						</Grid>
					</Grid>
				)}
			</>
		)
	}

	const renderManageContactInBoardActionSelectorInput = () => {
		const manageContactInBoardOptionActionToManageContactInBoardActionSelectorInput: Record<keyof ManageContactInBoardActionToManageContactInBoardData, () => JSX.Element> = {
			"add-or-move-contact-to-board-stage": () => getAddOrMoveContactToBoardSelectorInput(),
			"remove-contact-from-board": () => getRemoveContactFromBoardSelectorInput()
		}

		const currentManageContactInBoardAction = currentManageContactInBoardOption?.action

		if (currentManageContactInBoardAction) {
			const manageContactInBoardActionSelectorInput = manageContactInBoardOptionActionToManageContactInBoardActionSelectorInput[currentManageContactInBoardAction]

			if (manageContactInBoardActionSelectorInput) {
				return manageContactInBoardActionSelectorInput()
			}
		}
	}

	return (
		<BlockBodySectionEditor
			title="GERENCIAR CONTATO NO FUNIL"
			onSave={() => onSave(contentEditor.content)}
			onOpen={contentEditor.reset}
			AsideDrawerProps={{
				children: (
					<Loading.FallbackSpinner isLoading={isLoading}>
						<Grid
							container
							spacing={2}
						>
							<Grid
								item
								xs={12}
							>
								<Grid
									container
								>
									<SelectInput
										multiple={false}
										defaultOption={currentManageContactInBoardOption}
										onChange={option => (
											contentEditor.changeContent({
												manageContactInBoardData: {
													action: option?.action || null,
													data: null
												}
											})
										)}
										options={formattedManageContactInBoardOptions}
									/>
								</Grid>
							</Grid>

							{renderManageContactInBoardActionSelectorInput()}
						</Grid>
					</Loading.FallbackSpinner>
				)
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default ManageContactInBoardSectionEditor
